// Decides whether a teacher has a freshly-granted badge worth celebrating.
//
// Badges are only ever granted by a Diffit admin — one at a time via
// POST /admin/user/badges, or in bulk from a Google Sheet via POST /admin/badges
// — and the server sends nothing when that happens: no email, no Customer.io
// event, no push. So the app derives "you have a new badge" client-side by
// diffing `diffitUser.badges_json` (entries of `{ type, name, granted_at }`)
// against the `badge_seen_<type>` flags in `seen_nux_json`.

import { SEEN_NUX_KEYS } from './users';

// Badges granted at or before this instant are never announced. Badge granting
// shipped in Oct 2025, well before this notification did, so without a cutoff
// every existing Ambassador and Certified Educator would be congratulated for a
// badge they were given months ago the first time they loaded the app after
// deploy. Set to the notification's own ship date; safe to leave in place
// forever, and safe to move forward if the announcement is ever re-launched.
export const BADGE_NOTIFICATION_CUTOFF = new Date('2026-08-24T00:00:00Z');

/**
 * The `seen_nux_json` key recording that a badge type has been announced.
 *
 * @param {string} badgeType e.g. 'ambassador'
 * @returns {string} e.g. 'badge_seen_ambassador'
 */
export const badgeSeenNuxKey = (badgeType) => `${SEEN_NUX_KEYS.BADGE_SEEN_PREFIX}${badgeType}`;

/**
 * Parse a badge's `granted_at` into a Date.
 *
 * The API writes these with Python's `datetime.utcnow().isoformat()`, which
 * emits no timezone designator ("2026-08-24T13:45:35.060938"). Per the
 * ECMAScript Date Time String Format, an offset-less date-*time* is read as
 * LOCAL time — by both `new Date()` and date-fns' `parseISO`. For a teacher in
 * UTC-7 that reads a badge granted moments ago as seven hours in the past,
 * which is enough to land it on the wrong side of the cutoff and silently
 * swallow the announcement. Append the missing 'Z' so it's read as the UTC the
 * server actually meant.
 *
 * @param {string | null | undefined} value
 * @returns {Date | null} null for a nullish/empty input or an unparseable string.
 */
export function parseGrantedAt(value) {
  if (!value) return null;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  const date = new Date(hasZone ? value : `${value}Z`);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * The badges this user should be congratulated for right now.
 *
 * A badge qualifies when it was granted after BADGE_NOTIFICATION_CUTOFF and its
 * `badge_seen_<type>` flag isn't set. Returns every qualifying badge rather than
 * just the newest so that a bulk sheet import granting two badges at once shows
 * one combined modal — picking only the newest would either drip a second modal
 * on the next load or swallow the other badge entirely.
 *
 * A badge missing or with an unparseable `granted_at` is treated as old and
 * skipped: rows written by some future code path shouldn't fire a retroactive
 * congratulations for a badge the teacher may have held for years.
 *
 * @param {object | null | undefined} diffitUser
 * @returns {Array<{ type: string, name: string, granted_at: string }>} oldest grant first.
 */
export function getBadgesToAnnounce(diffitUser) {
  const badges = diffitUser?.badges_json;
  if (!Array.isArray(badges) || badges.length === 0) return [];
  const seenNux = diffitUser.seen_nux_json || {};

  return badges
    .filter((badge) => {
      if (!badge?.type) return false;
      if (seenNux[badgeSeenNuxKey(badge.type)]) return false;
      const grantedAt = parseGrantedAt(badge.granted_at);
      return !!grantedAt && grantedAt > BADGE_NOTIFICATION_CUTOFF;
    })
    .sort((a, b) => parseGrantedAt(a.granted_at) - parseGrantedAt(b.granted_at));
}
